import React from 'react'
import type { IChartApi, UTCTimestamp } from 'lightweight-charts'
import type { TF } from '@/modules/graph-view/types'
import { tfToSeconds } from '@/modules/graph-view/utils/normalize'
import { computeFiboProjectionTargets } from '../utils/fiboProjectionTargets'
import { computeFiboProjectionStop } from '../utils/fiboProjectionStop'

type Props = {
  chartRef: React.RefObject<IChartApi | null>
  candles: Array<{ time: number | UTCTimestamp }>
  timeframe: TF
  opportunity: any | null
  fibo: { hi?: number | null; lo?: number | null } | null
  enabled: boolean
}

type LevelDef = {
  key: string
  label: string
  value: number
  color: string
}

type SeriesHandle = {
  series: any
  priceLine?: any
}

function parseIsoToSeconds(iso?: string | null): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  return Math.floor(t / 1000)
}

function trendFromOpportunity(row: any): 'long' | 'short' {
  const raw = String(row?.target_trend ?? row?.confluence_factors?.trend_alvo ?? '').toLowerCase()
  const isShort = raw === 'bearish' || raw === 'baixa' || raw === 'short'
  return isShort ? 'short' : 'long'
}

const TARGET_COLOR = 'rgba(34,197,94,0.9)'
const STOP_COLOR = 'rgba(239,68,68,0.9)'

function buildLevels(
  opportunity: any | null,
  fibo: { hi?: number | null; lo?: number | null } | null,
): LevelDef[] {
  if (!opportunity || !fibo) return []

  const hi = Number(fibo.hi as any)
  const lo = Number(fibo.lo as any)
  const trend = trendFromOpportunity(opportunity)
  const targets = computeFiboProjectionTargets({ hi, lo, trend })
  if (!targets) return []

  const out: LevelDef[] = [
    { key: 't1', label: 'Alvo 1 (1.0)', value: targets.t1, color: TARGET_COLOR },
    { key: 't2', label: 'Alvo 2 (1.618)', value: targets.t2, color: TARGET_COLOR },
    { key: 't3', label: 'Alvo 3 (2.618)', value: targets.t3, color: TARGET_COLOR },
  ].filter((l) => Number.isFinite(l.value))

  const stop = computeFiboProjectionStop({ hi, lo, trend })
  if (stop != null && Number.isFinite(stop)) {
    out.push({ key: 'stop', label: 'Stop', value: stop, color: STOP_COLOR })
  }

  return out
}

export default function OpportunityFiboTargetsOverlay({
  chartRef,
  candles,
  timeframe,
  opportunity,
  fibo,
  enabled,
}: Props) {
  const seriesRef = React.useRef<SeriesHandle[]>([])

  React.useEffect(() => {
    const chartApi = chartRef.current

    const cleanup = () => {
      try {
        for (const h of seriesRef.current) {
          try {
            if (h.priceLine && h.series?.removePriceLine) {
              h.series.removePriceLine(h.priceLine)
            }
          } catch {}
          try {
            if (chartApi && h.series && chartApi.removeSeries) {
              chartApi.removeSeries(h.series)
            } else {
              h.series?.remove?.()
            }
          } catch {}
        }
      } catch {}
      seriesRef.current = []
    }

    if (!chartApi || !enabled || !candles || candles.length === 0) {
      cleanup()
      return () => cleanup()
    }

    const levels = buildLevels(opportunity, fibo)
    if (levels.length === 0) {
      cleanup()
      return () => cleanup()
    }

    cleanup()

    try {
      const times: number[] = []
      for (const c of candles as any[]) {
        const t = Number(c?.time)
        if (Number.isFinite(t)) times.push(t)
      }
      if (times.length === 0) return () => cleanup()
      times.sort((a, b) => a - b)

      const firstT = times[0]
      const lastT = times[times.length - 1]
      if (!Number.isFinite(firstT) || !Number.isFinite(lastT) || firstT >= lastT) {
        return () => cleanup()
      }

      const publishedIso = (opportunity as any)?.published_at ?? (opportunity as any)?.created_at ?? null
      const publishedSec = parseIsoToSeconds(publishedIso)

      let startTime = firstT
      if (publishedSec != null) {
        if (publishedSec <= firstT) {
          startTime = firstT
        } else if (publishedSec >= lastT) {
          startTime = lastT
        } else {
          for (const t of times) {
            if (t >= publishedSec) {
              startTime = t
              break
            }
          }
        }
      }
      if (startTime > lastT) return () => cleanup()

      const offsetBars = (() => {
        switch (timeframe) {
          case 'W':
            return 3
          case 'M':
            return 1
          default:
            return 12
        }
      })()
      const endTime = lastT + tfToSeconds(timeframe) * offsetBars

      for (const lv of levels) {
        try {
          const series = chartApi.addLineSeries({
            color: lv.color,
            lineWidth: 1,
            lineStyle: 2,
            lastValueVisible: false,
            priceLineVisible: false,
          } as any)

          try {
            series.setData([
              { time: startTime as UTCTimestamp, value: lv.value },
              { time: endTime as UTCTimestamp, value: lv.value },
            ])
          } catch {}

          let pl: any
          try {
            pl = series.createPriceLine({
              price: lv.value,
              color: lv.color,
              lineStyle: 2,
              lineWidth: 1,
              axisLabelVisible: true,
              title: lv.label,
            } as any)
          } catch {}

          seriesRef.current.push({ series, priceLine: pl })
        } catch {}
      }
    } catch {
      // em caso de falha silenciosa, apenas limpamos no cleanup
    }

    return () => cleanup()
  }, [chartRef, candles, timeframe, opportunity, fibo, enabled])

  return null
}
